import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleDocumentClick, { signal: this.#abortController.signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #handleDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : undefined;
    if (path ? path.includes(this) : this.contains(/** @type {Node} */ (event.target))) return;
    this.#deactivate();
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.#deactivate();
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    const sourceElement =
      event.currentTarget instanceof Element
        ? event.currentTarget
        : event.target instanceof Element
          ? event.target
          : null;

    if (event instanceof MouseEvent) {
      if (!sourceElement) return;

      const item = findMenuItem(sourceElement);
      if (!item) return;

      const submenu = findSubmenu(item) ?? (sourceElement.slot !== '' ? this.overflowMenu : null);

      if (!submenu) {
        this.#deactivate();
        return;
      }

      if (item === this.#state.activeItem) {
        this.#deactivate(item);
        return;
      }

      event.preventDefault();
      this.#shortDebouncedActivateHandler.cancel();
      this.#longDebouncedActivateHandler.cancel();

      this.#activateItem({ item, sourceElement, submenu });
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    const sourceElement =
      event.currentTarget instanceof Element
        ? event.currentTarget
        : event.target instanceof Element
          ? event.target
          : null;
    if (!sourceElement) return;

    const item = findMenuItem(sourceElement);
    if (!item || item === this.#state.activeItem) return;

    const submenu = findSubmenu(item) ?? (sourceElement.slot !== '' ? this.overflowMenu : null);

    this.#activateItem({ item, sourceElement, submenu });
  };

  #activateItem({ item, sourceElement, submenu }) {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    const isDefaultSlot = !sourceElement || sourceElement.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const effectiveSubmenu = submenu ?? findSubmenu(item) ?? (!isDefaultSlot ? this.overflowMenu : null);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
    const submenuHeight = effectiveSubmenu ? Math.max(effectiveSubmenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  }

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    const sourceElement =
      event.currentTarget instanceof Element
        ? event.currentTarget
        : event.target instanceof Element
          ? event.target
          : null;
    if (!sourceElement) return;

    const item = findMenuItem(sourceElement);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element;
  }

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const descendant = element.querySelector?.('[ref="menuitem"]');
  if (descendant) return descendant;

  return findMenuItem(element.parentElement);
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
